import { useEffect, useMemo, useState } from "react";
import { StyledAddProjectModal } from "./CollaboratorProjectModal.styled";
import { Modal } from "../../../../../../components/core/Modal/Modal";
import {
  ACTIVITIES_PERFORMED_NAME_LABEL,
  ADD_PROJECT_LABEL,
  CANCEL_LABEL,
  CUSTOMER_NAME_LABEL,
  CONFIRM_LABEL,
  DESCRIPTION_LABEL,
  EDIT_PROJECT_LABEL,
  ENDING_DATE_LABEL,
  IS_REQUIRED_LABEL,
  PLACEHOLDER_ACTIVITIES_PERFORMED_LABEL,
  PLACEHOLDER_DESCRIPTION_LABEL,
  PLACEHOLDER_TOOLS_NAME_LABEL,
  PROJECT_NAME_LABEL,
  ROLE_LABEL,
  SECTOR_NAME_LABEL,
  STARTING_DATE_LABEL,
  RESOURSES_POOL_NAME_LABEL,
  TODAY_LABEL,
  EXPERIENCE_LABEL,
  INVALID_DATE_LABEL,
  MUST_BE_AFTER_THAN_LABEL,
  MUST_BE_WITHIN_EXPERIENCE_LABEL,
} from "../../../../../../constants/Dictionary";
import {
  Button,
  ButtonVariants,
} from "../../../../../../components/core/Button/Button";
import { ReactComponent as CloseIcon } from "../../../../../../icons/close.svg";
import { ReactComponent as CheckIcon } from "../../../../../../icons/check.svg";
import {
  AddCollaboratorsProjects,
  CollaboratorHistoryProject,
  UpdateCollaboratorsProjectsRequest,
} from "../../../../../../types/api/projects.type";
import * as yup from "yup";
import { Controller, Resolver, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Loader } from "../../../../../../components/core/Loader/Loader";
import { FormField } from "../../../../../../components/core/FormField/FormField";
import dayjs from "dayjs";
import { AutoComplete } from "../../../../../../components/core/AutoComplete/AutoComplete";
import TextInput from "../../../../../../components/core/TextInput/TextInput";
import {
  CalendarInput,
  CalendarMode,
} from "../../../../../../components/core/CalendarInput/CalendarInput";
import TextArea from "../../../../../../components/core/TextArea/TextArea";
import {
  EN_DATE_FORMAT,
  isMonthSameOrAfter,
} from "../../../../../../utils/date/date.utils";
import {
  CollaboratorDetails,
  CollaboratorHistoryProfessionalExperience,
} from "../../../../../../types/api/collaborators.type";
import { shortDateFormat } from "../../../../../../constants/TableColumns";
import {
  Select,
  SelectVariantEnum,
} from "../../../../../../components/core/Select/Select";
import {
  useCompanyOptionsData,
  useHistoryProjectAdd,
  useHistoryProjectUpdate,
  useRoleOptionsData,
  useSectorOptionsData,
} from "../../../../../../hooks/projectsHistory.hook";
import { CHARACTER_MAX_LENGTH } from "../../../../../../constants/AppConstants";

type CollaboratorProjectModalProps = {
  isOpen: boolean;
  record?: CollaboratorHistoryProject | null;
  onSave: () => void;
  onClose: () => void;
  collaborator: CollaboratorDetails;
};

export type FormData = {
  name?: string;
  role: string;
  historyProfessionalExperienceId: number | null;
  sector: string;
  customer?: string;
  startDate: Date | null;
  endDate?: Date | null;
  activitiesPerformed: string;
  resourcesPool: string;
  projectDescription: string;
  isAbstract: boolean;
};

export const CollaboratorProjectModal = ({
  isOpen,
  record,
  onSave,
  onClose,
  collaborator,
}: CollaboratorProjectModalProps) => {
  const profileId = collaborator.id;

  const [historyProfessionalExperience, setHistoryProfessionalExperience] =
    useState<CollaboratorHistoryProfessionalExperience | null>(null);
  const [calendarBoxValueStartDate, setCalendarBoxValueStartDate] =
    useState<Date | null>(null);
  const [calendarBoxValueEndDate, setCalendarBoxValueEndDate] =
    useState<Date | null>(null);

  const {
    mutateAsync: historyProjectAdd,
    isPending: pendingHistoryProjectAdd,
  } = useHistoryProjectAdd(profileId);
  const {
    mutateAsync: historyProjectUpdate,
    isPending: pendingHistoryProjectUpdate,
  } = useHistoryProjectUpdate(profileId);

  const schema = yup
    .object({
      name: yup.string().when(["historyProfessionalExperienceId"], {
        is: (historyProfessionalExperienceId: number) => {
          const historyProfessionalExperience = companyOptions.find(
            (item) => item.id === historyProfessionalExperienceId,
          );
          return (
            historyProfessionalExperience?.isAbstract &&
            historyProfessionalExperience?.endDate === null
          );
        },
        then: (field) =>
          field.required(`${PROJECT_NAME_LABEL} ${IS_REQUIRED_LABEL}`),
        otherwise: (field) => field.optional(),
      }),
      historyProfessionalExperienceId: yup
        .number()
        .nullable()
        .required(`${EXPERIENCE_LABEL} ${IS_REQUIRED_LABEL}`),
      sector: yup
        .string()
        .required(`${SECTOR_NAME_LABEL} ${IS_REQUIRED_LABEL}`),
      role: yup.string().required(`${ROLE_LABEL} ${IS_REQUIRED_LABEL}`),
      customer: yup.string().optional(),
      startDate: yup
        .date()
        .nullable()
        .typeError(`${INVALID_DATE_LABEL}`)
        .required(`${STARTING_DATE_LABEL} ${IS_REQUIRED_LABEL}`)
        .test(
          "startDate-within-experience",
          `${STARTING_DATE_LABEL} ${MUST_BE_WITHIN_EXPERIENCE_LABEL}`,
          function (startDate) {
            if (!startDate || !historyProfessionalExperience) return true;
            const min = new Date(historyProfessionalExperience.startDate);
            const max = historyProfessionalExperience.endDate
              ? new Date(historyProfessionalExperience.endDate)
              : new Date();
            return (
              isMonthSameOrAfter(startDate, min) &&
              isMonthSameOrAfter(max, startDate)
            );
          },
        ),
      endDate: yup
        .date()
        .nullable()
        .typeError(`${INVALID_DATE_LABEL}`)
        .when(["historyProfessionalExperienceId"], {
          is: (historyProfessionalExperienceId: number) => {
            const historyProfessionalExperience = companyOptions.find(
              (item) => item.id === historyProfessionalExperienceId,
            );
            return historyProfessionalExperience?.endDate !== null;
          },
          then: (field) =>
            field.required(`${ENDING_DATE_LABEL} ${IS_REQUIRED_LABEL}`),
          otherwise: (field) => field.optional(),
        })
        .test(
          "endDate-within-experience",
          `${ENDING_DATE_LABEL} ${MUST_BE_WITHIN_EXPERIENCE_LABEL}`,
          function (endDate) {
            if (!endDate || !historyProfessionalExperience) return true;
            const min = new Date(historyProfessionalExperience.startDate);
            const max = historyProfessionalExperience.endDate
              ? new Date(historyProfessionalExperience.endDate)
              : new Date();
            return (
              isMonthSameOrAfter(endDate, min) &&
              isMonthSameOrAfter(max, endDate)
            );
          },
        )
        .test(
          "is-after-startDate",
          `La ${ENDING_DATE_LABEL} ${MUST_BE_AFTER_THAN_LABEL} ${STARTING_DATE_LABEL}.`,
          function (endDate) {
            const { startDate } = this.parent;
            if (!endDate || !startDate) return true;
            return isMonthSameOrAfter(endDate, startDate);
          },
        ),
      activitiesPerformed: yup
        .string()
        .required(`${ACTIVITIES_PERFORMED_NAME_LABEL} ${IS_REQUIRED_LABEL}`),
      resourcesPool: yup
        .string()
        .required(`${RESOURSES_POOL_NAME_LABEL} ${IS_REQUIRED_LABEL}`),
      projectDescription: yup
        .string()
        .required(`${DESCRIPTION_LABEL} ${IS_REQUIRED_LABEL}`),
      isAbstract: yup.boolean().default(false),
    })
    .required();

  const {
    handleSubmit,
    reset,
    control,
    setValue,
    watch,
    getValues,
    formState: { errors, isDirty, isSubmitted },
  } = useForm<FormData>({
    resolver: yupResolver(schema) as Resolver<FormData>,
  });

  useEffect(() => {
    if (record) {
      reset({
        name: record.projectItem.name || "",
        historyProfessionalExperienceId:
          record.historyProfessionalExperience.id,
        sector: record.projectItem.sector,
        role: record.projectItem.projectRole,
        customer: record.projectItem.customer || "",
        startDate: dayjs(record.projectItem.startDate).toDate(),
        endDate: record.projectItem.endDate
          ? dayjs(record.projectItem.endDate).toDate()
          : null,
        activitiesPerformed: record.projectItem.activitiesPerformed,
        resourcesPool: record.projectItem.resourcesPool,
        projectDescription: record.projectItem.projectDescription,
        isAbstract: record.historyProfessionalExperience.isAbstract,
      });
      setHistoryProfessionalExperience(record.historyProfessionalExperience);
    } else {
      reset({
        name: "",
        historyProfessionalExperienceId: null,
        sector: "",
        role: "",
        customer: "",
        startDate: null,
        endDate: null,
        activitiesPerformed: "",
        resourcesPool: "",
        projectDescription: "",
        isAbstract: false,
      });
    }
  }, [record]);

  const onSubmit: SubmitHandler<FormData> = async (data: FormData) => {
    if (!record) {
      const addBody: AddCollaboratorsProjects = {
        projectItem: {
          name: data.name ?? null,
          projectRole: data.role,
          startDate: dayjs(data.startDate).format(EN_DATE_FORMAT),
          endDate: data.endDate
            ? dayjs(data.endDate).format(EN_DATE_FORMAT)
            : null,
          customer: data.customer ?? null,
          sector: data.sector,
          projectDescription: data.projectDescription,
          activitiesPerformed: data.activitiesPerformed,
          resourcesPool: data.resourcesPool,
        },
        historyProfessionalExperienceId: data.historyProfessionalExperienceId!,
      };

      await historyProjectAdd(addBody);
    } else {
      const body: UpdateCollaboratorsProjectsRequest = {
        projectItem: {
          name: data.name,
          projectRole: data.role,
          startDate: dayjs(data.startDate).format(EN_DATE_FORMAT),
          endDate: data.endDate
            ? dayjs(data.endDate).format(EN_DATE_FORMAT)
            : null,
          customer: data.customer,
          sector: data.sector,
          projectDescription: data.projectDescription,
          activitiesPerformed: data.activitiesPerformed,
          resourcesPool: data.resourcesPool,
        },
        historyProfessionalExperienceId: data.historyProfessionalExperienceId!,
      };
      await historyProjectUpdate({ projectId: record.projectId, body });
    }
    onSave();
    closeModal();
  };

  const closeModal = () => {
    onClose();
  };

  const {
    data: rolesOptions,
    isLoading: rolesInitLoading,
    hasNextPage: loadMoreRoles,
    setSearch: setRoleSearchValue,
    clearSearch: clearRolesSearch,
    fetchNextPage: fetchMoreRoles,
    isFetchingNextPage: fetchingMoreRoles,
  } = useRoleOptionsData();

  const {
    data: sectorsOptions,
    isLoading: sectorsInitLoading,
    hasNextPage: loadMoreSectors,
    setSearch: setSectorsSearchValue,
    clearSearch: clearSectorsSearch,
    fetchNextPage: fetchMoreSectors,
    isFetchingNextPage: fetchingMoreSectors,
  } = useSectorOptionsData();

  const {
    data: companyOptions,
    isLoading: companyInitLoading,
    hasNextPage: loadMoreCompany,
    setSearch: setCompanySearchValue,
    search: companySearchValue,
    fetchNextPage: fetchMoreCompany,
    isFetchingNextPage: fetchingMoreCompany,
  } = useCompanyOptionsData(profileId);

  const formatCompanyLabel = (
    item: CollaboratorHistoryProfessionalExperience,
  ) => {
    const company = item.companyName;
    const startDate = shortDateFormat(item.startDate);
    const endDate = item.endDate ? shortDateFormat(item.endDate) : TODAY_LABEL;

    return `${company} | ${startDate} - ${endDate}`;
  };

  const experience = watch("historyProfessionalExperienceId");

  const isCompanySelected = experience != null;

  const experienceOptions =
    record && record.historyProfessionalExperience
      ? [
          record.historyProfessionalExperience,
          ...companyOptions.filter(
            (item) => item.id !== record.historyProfessionalExperience.id,
          ),
        ]
      : companyOptions;

  const handleExperienceChange = (experienceId: number | null) => {
    const experience =
      experienceOptions.find((item) => item.id === experienceId) ?? null;
    const isAbstract = experience?.isAbstract ?? false;
    const startDate = experience?.startDate
      ? dayjs(experience.startDate).date(1).toDate()
      : null;
    const endDate = experience?.endDate
      ? dayjs(experience.endDate).date(1).toDate()
      : null;

    setHistoryProfessionalExperience(experience);

    setCalendarBoxValueStartDate(startDate);
    setCalendarBoxValueEndDate(endDate);
    setValue("isAbstract", isAbstract, { shouldValidate: true });
    if (isSubmitted) {
      setValue("name", getValues("name"), { shouldValidate: true });
      setValue("endDate", getValues("endDate"), { shouldValidate: true });
    }
  };

  const startDate = watch("startDate");

  const minEndDate = useMemo(() => {
    const validStartDate = startDate && !isNaN(startDate.getTime());
    if (!historyProfessionalExperience) {
      return validStartDate ? startDate : undefined;
    }
    if (
      validStartDate &&
      isMonthSameOrAfter(
        startDate,
        new Date(historyProfessionalExperience.startDate),
      )
    ) {
      return startDate;
    }
    return new Date(historyProfessionalExperience.startDate);
  }, [startDate, historyProfessionalExperience]);

  return (
    <Modal
      fullSize={false}
      label={record ? EDIT_PROJECT_LABEL : ADD_PROJECT_LABEL}
      isOpen={isOpen}
      leftButton={
        <Button
          label={CANCEL_LABEL}
          variant={ButtonVariants.SECONDARY}
          icon={<CloseIcon />}
          onClick={closeModal}
          disabled={pendingHistoryProjectAdd || pendingHistoryProjectUpdate}
        />
      }
      rightButton={
        <Button
          label={CONFIRM_LABEL}
          variant={ButtonVariants.PRIMARY}
          icon={<CheckIcon />}
          disabled={
            !isDirty || pendingHistoryProjectAdd || pendingHistoryProjectUpdate
          }
          type="submit"
          form="projectForm"
        />
      }
    >
      <StyledAddProjectModal>
        <Loader
          loading={pendingHistoryProjectAdd || pendingHistoryProjectUpdate}
        >
          <form id="projectForm" onSubmit={handleSubmit(onSubmit)}>
            <div className="input-row">
              <FormField error={errors.name?.message}>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => {
                    return (
                      <TextInput
                        label={PROJECT_NAME_LABEL}
                        maxLength={255}
                        {...field}
                      />
                    );
                  }}
                />
              </FormField>
              <FormField
                error={errors.historyProfessionalExperienceId?.message}
              >
                <Controller
                  name="historyProfessionalExperienceId"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Select
                      variant={SelectVariantEnum.DEFAULT}
                      label={EXPERIENCE_LABEL}
                      options={experienceOptions.map((item) => ({
                        value: item.id,
                        label: formatCompanyLabel(item),
                      }))}
                      value={value}
                      onChange={(value) => {
                        onChange(value);
                        handleExperienceChange(value);
                      }}
                      onClear={() => {
                        setHistoryProfessionalExperience(null);
                      }}
                      withSearch
                      searchValue={companySearchValue}
                      onSearch={setCompanySearchValue}
                      loading={companyInitLoading}
                      fetchMore={fetchMoreCompany}
                      canLoadMore={loadMoreCompany}
                      fetchingMore={fetchingMoreCompany}
                    />
                  )}
                />
              </FormField>
            </div>
            <div className="input-row">
              <FormField error={errors.sector?.message}>
                <Controller
                  name="sector"
                  control={control}
                  render={({ field }) => (
                    <AutoComplete
                      label={SECTOR_NAME_LABEL}
                      options={sectorsOptions.map((role) => ({
                        value: role,
                        label: role,
                      }))}
                      {...field}
                      onChange={(value) => {
                        field.onChange(value.toUpperCase());
                        setSectorsSearchValue(value);
                      }}
                      onClear={clearSectorsSearch}
                      loading={sectorsInitLoading}
                      fetchMore={fetchMoreSectors}
                      canLoadMore={loadMoreSectors}
                      fetchingMore={fetchingMoreSectors}
                    />
                  )}
                />
              </FormField>
              <FormField error={errors.role?.message}>
                <Controller
                  name="role"
                  control={control}
                  render={({ field }) => (
                    <AutoComplete
                      label={ROLE_LABEL}
                      options={rolesOptions.map((role) => ({
                        value: role,
                        label: role,
                      }))}
                      {...field}
                      onChange={(value) => {
                        field.onChange(value.toUpperCase());
                        setRoleSearchValue(value);
                      }}
                      onClear={clearRolesSearch}
                      loading={rolesInitLoading}
                      fetchMore={fetchMoreRoles}
                      canLoadMore={loadMoreRoles}
                      fetchingMore={fetchingMoreRoles}
                    />
                  )}
                />
              </FormField>
              <FormField error={errors.customer?.message}>
                <Controller
                  name="customer"
                  control={control}
                  render={({ field }) => {
                    return (
                      <TextInput
                        label={CUSTOMER_NAME_LABEL}
                        maxLength={255}
                        {...field}
                      />
                    );
                  }}
                />
              </FormField>
            </div>
            <div className="input-row">
              <FormField error={errors.startDate?.message}>
                <Controller
                  name="startDate"
                  control={control}
                  render={({ field }) => (
                    <CalendarInput
                      label={STARTING_DATE_LABEL}
                      pickerMode={CalendarMode.MONTHS}
                      minDate={
                        historyProfessionalExperience
                          ? new Date(historyProfessionalExperience.startDate)
                          : undefined
                      }
                      maxDate={
                        historyProfessionalExperience?.endDate
                          ? new Date(historyProfessionalExperience.endDate)
                          : new Date()
                      }
                      disabled={!isCompanySelected}
                      defaultPickerValue={calendarBoxValueStartDate}
                      {...field}
                    />
                  )}
                />
              </FormField>
              <FormField error={errors.endDate?.message}>
                <Controller
                  name="endDate"
                  control={control}
                  render={({ field }) => (
                    <CalendarInput
                      label={ENDING_DATE_LABEL}
                      pickerMode={CalendarMode.MONTHS}
                      minDate={minEndDate}
                      maxDate={
                        historyProfessionalExperience?.endDate
                          ? new Date(historyProfessionalExperience.endDate)
                          : new Date()
                      }
                      disabled={!isCompanySelected}
                      defaultPickerValue={calendarBoxValueEndDate}
                      {...field}
                    />
                  )}
                />
              </FormField>
            </div>
            <FormField error={errors.activitiesPerformed?.message}>
              <Controller
                name="activitiesPerformed"
                control={control}
                render={({ field }) => {
                  return (
                    <TextArea
                      label={PLACEHOLDER_ACTIVITIES_PERFORMED_LABEL}
                      maxLength={CHARACTER_MAX_LENGTH}
                      {...field}
                    />
                  );
                }}
              />
            </FormField>
            <FormField error={errors.resourcesPool?.message}>
              <Controller
                name="resourcesPool"
                control={control}
                render={({ field }) => (
                  <TextArea
                    label={PLACEHOLDER_TOOLS_NAME_LABEL}
                    maxLength={CHARACTER_MAX_LENGTH}
                    {...field}
                  />
                )}
              />
            </FormField>
            <FormField error={errors.projectDescription?.message}>
              <Controller
                name="projectDescription"
                control={control}
                render={({ field }) => (
                  <TextArea
                    label={PLACEHOLDER_DESCRIPTION_LABEL}
                    maxLength={CHARACTER_MAX_LENGTH}
                    {...field}
                  />
                )}
              />
            </FormField>
          </form>
        </Loader>
      </StyledAddProjectModal>
    </Modal>
  );
};
